import React, { useMemo, useState, useCallback, useEffect } from "react";
import Map, { Source, Layer, Popup, NavigationControl, FullscreenControl, ScaleControl, GeolocateControl } from "react-map-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import supercluster from "supercluster";

// Add mobile detection
const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
};

const clusterLayer = {
    id: 'clusters',
    type: 'circle',
    source: 'farms',
    filter: ['>', ['get', 'point_count'], 1],
    paint: {
        'circle-color': [
            'step',
            ['get', 'point_count'],
            '#51bbd6', 10,
            '#f1f075', 50,
            '#f28cb1'
        ],
        'circle-radius': [
            'step',
            ['get', 'point_count'],
            30,
            40,
            50
        ]
    }
};

const pointCountLayer = {
    id: 'point-count',
    type: 'symbol',
    source: 'farms',
    filter: ['>', ['get', 'point_count'], 1],
    layout: {
        'text-field': ['get', 'point_count'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
    },
    paint: {
        'text-color': '#000'
    }
};

const unclusteredPointLayer = {
    id: 'unclustered-point',
    type: 'circle',
    source: 'farms',
    filter: ['!', ['has', 'point_count']],
    paint: {
        'circle-color': '#11b4da',
        'circle-radius': 8,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff'
    }
};

const unclusteredPointCountLayer = {
    id: 'unclustered-point-count',
    type: 'symbol',
    source: 'farms',
    filter: ['!', ['has', 'point_count']],
    layout: {
        'text-field': '1',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
    },
    paint: {
        'text-color': '#000'
    }
};

const FullScreenMap = ({ farmLocations, selectedCountries, selectedStates, selectedCities, selectedFarmTypes, searchQuery, popupInfo, setPopupInfo, apiKey }) => {
    const [viewState, setViewState] = useState({
        longitude: 0,
        latitude: 0,
        zoom: 5
    });
    const [clusters, setClusters] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapError, setMapError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [layerVisibility, setLayerVisibility] = useState({
        'satellite-test-layer': true, // ArcGIS satellite as base
        'roads-layer': true, // Mapbox roads overlay
        'labels-layer': true, // Mapbox labels overlay
        'gfw-forest-loss': false,
        'gfw-glad-alerts': false
    });



    // Calculate centroid and optimal zoom level of all farm locations
    const { centroid, optimalZoom } = useMemo(() => {
        if (farmLocations.length === 0) return { centroid: { lon: 0, lat: 0 }, optimalZoom: 5 };

        const validFarms = farmLocations.filter(farm => {
            const lat = Number(farm.lat);
            const lon = Number(farm.lon);
            return !isNaN(lat) && !isNaN(lon);
        });

        if (validFarms.length === 0) return { centroid: { lon: 0, lat: 0 }, optimalZoom: 5 };

        // Calculate centroid
        const totalCoords = validFarms.reduce(
            (acc, farm) => {
                acc.lat += Number(farm.lat);
                acc.lon += Number(farm.lon);
                return acc;
            },
            { lat: 0, lon: 0 }
        );

        const centroid = {
            lat: totalCoords.lat / validFarms.length,
            lon: totalCoords.lon / validFarms.length
        };

        // Calculate optimal zoom level based on spread of coordinates
        if (validFarms.length === 1) {
            // Single farm - use moderate zoom
            return { centroid, optimalZoom: isMobile() ? 9 : 11 };
        }

        // Calculate bounding box
        const lats = validFarms.map(farm => Number(farm.lat));
        const lons = validFarms.map(farm => Number(farm.lon));
        
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLon = Math.min(...lons);
        const maxLon = Math.max(...lons);

        // Calculate the span of coordinates
        const latSpan = maxLat - minLat;
        const lonSpan = maxLon - minLon;
        const maxSpan = Math.max(latSpan, lonSpan);

        // Calculate optimal zoom level based on span
        // Reduced zoom levels to be less close
        let zoom;
        if (maxSpan > 10) {
            zoom = isMobile() ? 4 : 5; // Very spread out farms
        } else if (maxSpan > 5) {
            zoom = isMobile() ? 5 : 6; // Moderately spread out
        } else if (maxSpan > 2) {
            zoom = isMobile() ? 6 : 7; // Somewhat spread out
        } else if (maxSpan > 1) {
            zoom = isMobile() ? 7 : 8; // Close together
        } else if (maxSpan > 0.5) {
            zoom = isMobile() ? 8 : 9; // Very close together
        } else {
            zoom = isMobile() ? 9 : 10; // Extremely close together
        }

        return { centroid, optimalZoom: zoom };
    }, [farmLocations]);

    // Apply filters to farm locations based on dropdown selections and search query
    const filteredLocations = useMemo(() => {
        return farmLocations.filter((farm) => {
            const matchesCountry = selectedCountries.length === 0 || selectedCountries.includes(farm.country);
            const matchesState = selectedStates.length === 0 || selectedStates.includes(farm.state);
            const matchesCity = selectedCities.length === 0 || selectedCities.includes(farm.city);
            const matchesType = selectedFarmTypes.length === 0 || selectedFarmTypes.includes(farm.type);
            const searchQueryLower = searchQuery.toLowerCase();
            const matchesSearch =
                searchQueryLower === '' ||
                farm.name.toLowerCase().includes(searchQueryLower) ||
                farm.country.toLowerCase().includes(searchQueryLower) ||
                farm.state.toLowerCase().includes(searchQueryLower) ||
                farm.city.toLowerCase().includes(searchQueryLower) ||
                farm.type.toLowerCase().includes(searchQueryLower);

            return matchesCountry && matchesState && matchesCity && matchesType && matchesSearch;
        });
    }, [farmLocations, selectedCountries, selectedStates, selectedCities, selectedFarmTypes, searchQuery]);

    // Generate points and create supercluster instance
    const points = useMemo(() => filteredLocations.map((farm) => ({
        type: "Feature",
        properties: { farmId: farm.id, name: farm.name, country: farm.country, state: farm.state, city: farm.city, type: farm.type },
        geometry: {
            type: "Point",
            coordinates: [Number(farm.lon), Number(farm.lat)]
        }
    })), [filteredLocations]);

    const clusterIndex = useMemo(() => {
        const index = new supercluster({
            radius: 100,
            maxZoom: 16
        });
        index.load(points);
        return index;
    }, [points]);

    // Update clusters based on view
    const updateClusters = useCallback(() => {
        if (!mapLoaded) return;
        
        const bbox = [
            viewState.bounds?._sw?.lng || -180,
            viewState.bounds?._sw?.lat || -90,
            viewState.bounds?._ne?.lng || 180,
            viewState.bounds?._ne?.lat || 90
        ];
        const zoom = Math.floor(viewState.zoom);
        setClusters(clusterIndex.getClusters(bbox, zoom));
    }, [viewState, clusterIndex, mapLoaded]);

    useEffect(() => {
        updateClusters();
    }, [viewState, updateClusters]);



    // Set viewState based on the centroid and optimal zoom of the farm locations
    useEffect(() => {
        if (filteredLocations.length === 0) {
            // If no filtered farms, use centroid of all farms (or fallback to Nigeria center)
            if (farmLocations.length > 0) {
                const lats = farmLocations.map(farm => Number(farm.lat));
                const lons = farmLocations.map(farm => Number(farm.lon));
                const minLat = Math.min(...lats);
                const maxLat = Math.max(...lats);
                const minLon = Math.min(...lons);
                const maxLon = Math.max(...lons);
                const centerLat = (minLat + maxLat) / 2;
                const centerLon = (minLon + maxLon) / 2;
                setViewState({ longitude: centerLon, latitude: centerLat, zoom: 5 });
            } else {
                setViewState({ longitude: 8.6753, latitude: 9.082, zoom: 5 }); // Nigeria center fallback
            }
            return;
        }
        if (filteredLocations.length === 1) {
            // Single farm: max zoom
            setViewState({
                longitude: Number(filteredLocations[0].lon),
                latitude: Number(filteredLocations[0].lat),
                zoom: 16,
                bearing: 0,
                pitch: 0
            });
            return;
        }
        // Multiple farms: fit bounds
        const lats = filteredLocations.map(farm => Number(farm.lat));
        const lons = filteredLocations.map(farm => Number(farm.lon));
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLon = Math.min(...lons);
        const maxLon = Math.max(...lons);
        // Center
        const centerLat = (minLat + maxLat) / 2;
        const centerLon = (minLon + maxLon) / 2;
        // Estimate zoom based on spread
        const latSpan = maxLat - minLat;
        const lonSpan = maxLon - minLon;
        const maxSpan = Math.max(latSpan, lonSpan);
        let zoom;
        if (maxSpan > 10) {
            zoom = isMobile() ? 4 : 5;
        } else if (maxSpan > 5) {
            zoom = isMobile() ? 5 : 6;
        } else if (maxSpan > 2) {
            zoom = isMobile() ? 6 : 7;
        } else if (maxSpan > 1) {
            zoom = isMobile() ? 7 : 8;
        } else if (maxSpan > 0.5) {
            zoom = isMobile() ? 8 : 9;
        } else {
            zoom = isMobile() ? 9 : 10;
        }
        setViewState({
            longitude: centerLon,
            latitude: centerLat,
            zoom,
            bearing: 0,
            pitch: 0
        });
    }, [filteredLocations, farmLocations]);



    // Handle map load
    const handleMapLoad = useCallback((event) => {
        setMapLoaded(true);
        setMapError(false);
        updateClusters();
        window.mapboxglMap = event.target;
    }, [updateClusters]);

    // Handle map error
    const handleMapError = useCallback((error) => {
        console.error('Map loading error:', error);
        setMapError(true);
        
        // Retry on mobile with a delay
        if (isMobile() && retryCount < 3) {
            setTimeout(() => {
                setRetryCount(prev => prev + 1);
                setMapLoaded(false);
                setMapError(false);
            }, 2000);
        }
    }, [retryCount]);

    // Mobile-specific map configuration
    const mapConfig = {
        ...viewState,
        onMove: (evt) => setViewState(evt.viewState),
        mapStyle: "mapbox://styles/mapbox/light-v11", // Use light style as base, we'll overlay Sentinel satellite
        mapboxAccessToken: apiKey,
        style: { width: '100vw', height: '100vh' },
        onLoad: handleMapLoad,
        onError: handleMapError,
        // Mobile-specific settings
        dragPan: true,
        dragRotate: false, // Disable rotation on mobile
        scrollZoom: true,
        touchZoomRotate: true,
        doubleClickZoom: true,
        keyboard: false, // Disable keyboard on mobile
        // Performance optimizations for mobile
        maxZoom: 16,
        minZoom: 1,
        // Mobile viewport settings
        viewport: {
            width: window.innerWidth,
            height: window.innerHeight
        }
    };

    // Show error message if map fails to load
    if (mapError && retryCount >= 3) {
        return (
            <div style={{ 
                height: '100vh', 
                width: '100vw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#181F2A',
                color: '#fff',
                flexDirection: 'column',
                padding: '20px',
                textAlign: 'center'
            }}>
                <h3>Map Loading Error</h3>
                <p>Please refresh the page or check your internet connection.</p>
                <button 
                    onClick={() => {
                        setRetryCount(0);
                        setMapError(false);
                        setMapLoaded(false);
                    }}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#F9AA33',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        marginTop: '10px'
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div style={{ 
            height: '100vh', 
            width: '100vw',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <Map {...mapConfig}>
                {/* Base satellite layer - rendered first (bottom) */}
                <Source
                    id="satellite-test"
                    type="raster"
                    tiles={['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}']}
                    tileSize={256}
                >
                    <Layer 
                        id="satellite-test-layer" 
                        type="raster" 
                        source="satellite-test" 
                        paint={{ 'raster-opacity': 1.0 }} 
                        layout={{ visibility: layerVisibility['satellite-test-layer'] ? 'visible' : 'none' }} 
                    />
                </Source>

                {/* Global Forest Watch Layers - rendered on top of satellite */}
                <Source
                    id="gfw-forest-loss"
                    type="raster"
                    tiles={['https://tiles.globalforestwatch.org/umd_tree_cover_loss/latest/tc_loss/{z}/{x}/{y}.png?threshold=30']}
                    tileSize={256}
                >
                    <Layer 
                        id="gfw-forest-loss" 
                        type="raster" 
                        source="gfw-forest-loss" 
                        paint={{ 'raster-opacity': 0.5 }} 
                        layout={{ visibility: layerVisibility['gfw-forest-loss'] ? 'visible' : 'none' }} 
                    />
                </Source>

                <Source
                    id="gfw-glad-alerts"
                    type="raster"
                    tiles={['https://tiles.globalforestwatch.org/umd_glad_landsat_alerts/latest/dynamic/{z}/{x}/{y}.png']}
                    tileSize={256}
                >
                    <Layer 
                        id="gfw-glad-alerts" 
                        type="raster" 
                        source="gfw-glad-alerts" 
                        paint={{ 'raster-opacity': 0.9 }} 
                        layout={{ visibility: layerVisibility['gfw-glad-alerts'] ? 'visible' : 'none' }} 
                    />
                </Source>

                {/* Mapbox roads and labels overlay */}
                <Source
                    id="mapbox-roads"
                    type="vector"
                    url="mapbox://mapbox.mapbox-streets-v8"
                >
                    <Layer 
                        id="roads-layer" 
                        type="line" 
                        source="mapbox-roads"
                        source-layer="road"
                        paint={{ 
                            'line-color': '#ffffff',
                            'line-width': 2,
                            'line-opacity': 0.5
                        }}
                        layout={{ 
                            visibility: layerVisibility['roads-layer'] ? 'visible' : 'none',
                            'line-join': 'round',
                            'line-cap': 'round'
                        }}
                    />
                </Source>

                <Source
                    id="mapbox-labels"
                    type="vector"
                    url="mapbox://mapbox.mapbox-streets-v8"
                >
                    <Layer 
                        id="labels-layer" 
                        type="symbol" 
                        source="mapbox-labels"
                        source-layer="place_label"
                        layout={{ 
                            'text-field': ['get', 'name'],
                            'text-size': 12,
                            'text-color': '#ffffff',
                            'text-halo-color': '#000000',
                            'text-halo-width': 1,
                            'visibility': layerVisibility['labels-layer'] ? 'visible' : 'none'
                        }}
                        paint={{ 
                            'text-color': '#ffffff',
                            'text-halo-color': '#000000',
                            'text-halo-width': 1
                        }}
                    />
                </Source>

                {/* Farm clusters - rendered last to ensure they're always on top */}
                <Source
                    id="farms"
                    type="geojson"
                    data={{
                        type: "FeatureCollection",
                        features: clusters || []
                    }}
                    cluster={true}
                    clusterMaxZoom={14}
                    clusterRadius={isMobile() ? 80 : 50} // Smaller cluster radius on mobile
                >
                    <Layer {...clusterLayer} />
                    <Layer {...pointCountLayer} />
                    <Layer {...unclusteredPointLayer} />
                    <Layer {...unclusteredPointCountLayer} />
                </Source>

                {popupInfo && (
                    <Popup
                        longitude={Number(popupInfo.lon)}
                        latitude={Number(popupInfo.lat)}
                        onClose={() => setPopupInfo(null)}
                        closeButton={true}
                        closeOnClick={false}
                        anchor="bottom"
                        offset={10}
                    >
                        <div style={{ 
                            padding: '8px', 
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#333'
                        }}>
                            {popupInfo.name}
                        </div>
                    </Popup>
                )}

                <GeolocateControl 
                    position="top-left" 
                    showUserLocation={true}
                    showAccuracyCircle={false}
                />
                <FullscreenControl position="top-left" />
                <NavigationControl 
                    position="top-left"
                    showCompass={false} // Hide compass on mobile
                    showZoom={true}
                />
                <ScaleControl />
            </Map>

            <div style={{ 
                position: 'absolute', 
                top: 160, 
                left: 10, 
                zIndex: 1100, 
                backgroundColor: 'white', 
                padding: '12px', 
                borderRadius: '8px', 
                fontSize: '13px', 
                width: '220px', 
                lineHeight: '1.4',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                border: '1px solid #e0e0e0'
            }}>
                <div style={{ marginBottom: '10px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
                    <strong style={{ fontSize: '16px', color: '#333' }}>🌍 Satellite Layers</strong>
                </div>
                <div style={{ 
                    marginBottom: '10px', 
                    padding: '8px', 
                    backgroundColor: '#d4edda', 
                    border: '1px solid #c3e6cb', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#155724'
                }}>
                    🌳 Global Forest Watch layers for deforestation monitoring. Tree cover loss, current tree cover, and real-time alerts.
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                    <strong style={{ fontSize: '14px', color: '#555' }}>Layer Visibility</strong>
                    {Object.entries(layerVisibility).map(([layerId, visible]) => (
                        <div key={layerId} style={{ marginTop: '8px', display: 'flex', alignItems: 'center' }}>
                            <input
                                type="checkbox"
                                checked={visible}
                                onChange={() => {
                                    console.log(`Toggling ${layerId} from ${visible} to ${!visible}`);
                                    setLayerVisibility(prev => ({ ...prev, [layerId]: !prev[layerId] }));
                                    // Add a small delay to ensure the layer exists
                                    setTimeout(() => {
                                        try {
                                            if (window.mapboxglMap?.getLayer(layerId)) {
                                                window.mapboxglMap.setLayoutProperty(layerId, 'visibility', !visible ? 'visible' : 'none');
                                                console.log(`Layer ${layerId} visibility set to ${!visible ? 'visible' : 'none'}`);
                                            } else {
                                                console.warn(`Layer ${layerId} not found on map`);
                                            }
                                        } catch (error) {
                                            console.warn(`Error toggling layer ${layerId}:`, error);
                                        }
                                    }, 100);
                                }}
                                style={{ marginRight: '8px' }}
                            />
                            <span style={{ fontSize: '13px', color: '#666' }}>
                                {layerId === 'satellite-test-layer' ? '🛰️ SATELLITE' : 
                                 layerId === 'roads-layer' ? '🛣️ ROADS' :
                                 layerId === 'labels-layer' ? '🏷️ LABELS' :
                                 layerId === 'gfw-forest-loss' ? '🌳 FOREST LOSS (2001-2023)' :
                                 layerId === 'gfw-glad-alerts' ? '🚨 GLAD ALERTS (2021+)' :
                                 layerId.replace(/-/g, ' ').replace(/layer/g, '').toUpperCase()}
                            </span>
                        </div>
                    ))}
                </div>


            </div>

            <div style={{ 
                position: 'absolute', 
                bottom: 120, 
                left: 10, 
                zIndex: 1000, 
                backgroundColor: 'white', 
                padding: '15px', 
                borderRadius: '8px', 
                fontSize: '14px', 
                maxWidth: '300px',
                minWidth: '250px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                border: '1px solid #e0e0e0'
            }}>
                <div style={{ marginBottom: '8px', borderBottom: '1px solid #e0e0e0', paddingBottom: '6px' }}>
                    <strong style={{ fontSize: '14px', color: '#333' }}>🌳 Forest Watch Legend</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                    <div style={{ width: 16, height: 16, backgroundColor: '#FF0000', marginRight: 8, borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '12px', color: '#666' }}>Forest Loss (2001-2023)</span>
                </div>
                <div style={{ 
                    marginTop: '4px', 
                    fontSize: '11px', 
                    color: '#888',
                    fontStyle: 'italic',
                    lineHeight: '1.3'
                }}>
                    Red intensity = Year (1=2001, 23=2023)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '6px' }}>
                    <div style={{ width: 16, height: 16, backgroundColor: '#FF00FF', marginRight: 8, borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '12px', color: '#666' }}>GLAD Alerts (Pink)</span>
                </div>
                <div style={{ 
                    marginTop: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                </div>
                <div style={{ 
                    marginTop: '0px', 
                    fontSize: '11px', 
                    color: '#888',
                    fontStyle: 'italic',
                    borderTop: '1px solid #eee',
                    paddingTop: '6px'
                }}>
                    Data: Global Forest Watch
                </div>
            </div>
        </div>
    );
};

export default FullScreenMap;
